import { cloneDeep, includes, isEmpty, get, concat, toNumber } from 'lodash'
import { WAM_THIRD_PARTY_TYPES, MEDIA_CONVERSION_TYPE, MEDIA_CAMPAIGN_TYPE } from '@/utils/constants'
import { errorHandler } from '@/utils/helpers'

const formulaToV1Formula = formula => {
    formula = cloneDeep(formula)
    let v1Formula = ['&']
    for (const inclusion of formula.inclusion) {
        const genericInclusion = ['|']
        for (const { criterion } of inclusion.criteria) {
            if (!criterion.isDisabled) {
                const inclusionCriteria = {
                    type: criterion.type,
                    ids: criterion.ids,
                    name: criterion.name,
                    recency: parseInt(criterion.recency)
                }
                //criteria_statistics does not accept range & level for non third party data
                if (includes(WAM_THIRD_PARTY_TYPES, criterion.type)) {
                    genericInclusion.push({
                        ...inclusionCriteria,
                        range: [parseInt(criterion.range), 20],
                        level: [parseInt(criterion.level), 20]
                    })
                } else if (criterion.type === 'SmartSegment') {
                    genericInclusion.push({
                        ...inclusionCriteria,
                        range: criterion.range
                    })
                } else {
                    genericInclusion.push(inclusionCriteria)
                }
            }
        }
        if (genericInclusion.length > 1) {
            v1Formula.push(genericInclusion)
        }
    }
    const isExclusionBoxDisabled = get(formula, 'exclusion[0].isDisabled', false)
    const exclusionCriteria = get(formula, 'exclusion[0].criteria', [])
    if (!isExclusionBoxDisabled && !isEmpty(exclusionCriteria)) {
        for (const exclusion of formula.exclusion) {
            const genericExclusion = ['!']
            for (const { criterion } of exclusion.criteria) {
                if (!criterion.isDisabled) {
                    const exclusionCriteria = {
                        type: criterion.type,
                        ids: criterion.ids,
                        name: criterion.name,
                        recency: parseInt(criterion.recency)
                    }
                    //criteria_statistics does not accept range & level for non third party data
                    if (includes(WAM_THIRD_PARTY_TYPES, criterion.type)) {
                        genericExclusion.push({
                            ...exclusionCriteria,
                            range: [parseInt(criterion.range), 20],
                            level: [parseInt(criterion.level), 20]
                        })
                    } else if (criterion.type === 'SmartSegment') {
                        genericExclusion.push({
                            ...exclusionCriteria,
                            range: criterion.range
                        })
                    } else {
                        genericExclusion.push(exclusionCriteria)
                    }
                }
            }
            v1Formula.push(genericExclusion)
        }
    }
    if (v1Formula.length > 1) {
        if (v1Formula.length === 2) v1Formula = v1Formula[1]
        return v1Formula
    } else {
        return ''
    }
}

const formulaToV2Formula = formula => {
    formula = cloneDeep(formula)
    const v2Formula = { operator: 'intersection', children: [] }
    for (const inclusion of formula.inclusion) {
        const genericInclusion = { operator: 'union', children: [] }
        for (const { criterion } of inclusion.criteria) {
            if (!criterion.isDisabled) {
                const inclusionCriteria = {
                    type: criterion.type,
                    id: criterion.ids,
                    recency: criterion.recency
                }
                //criteria_statistics does not accept range & level for non third party data
                if (includes(WAM_THIRD_PARTY_TYPES, criterion.type)) {
                    genericInclusion.children.push({
                        ...inclusionCriteria,
                        range: [parseInt(criterion.range), 20],
                        level: [parseInt(criterion.level), 20]
                    })
                } else if (criterion.type === 'SmartSegment') {
                    genericInclusion.children.push({
                        ...inclusionCriteria,
                        range: criterion.range
                    })
                } else {
                    genericInclusion.children.push(inclusionCriteria)
                }
            }
        }
        if (genericInclusion.children.length > 0) {
            v2Formula.children.push(genericInclusion)
        }
    }
    const isExclusionBoxDisabled = get(formula, 'exclusion[0].isDisabled', false)
    const formulaExclusionCriteria = get(formula, 'exclusion[0].criteria', [])
    if (!isExclusionBoxDisabled && !isEmpty(formulaExclusionCriteria)) {
        for (const exclusion of formula.exclusion) {
            const genericExclusion = {
                operator: 'exclusion',
                children: [{ operator: 'union', children: [] }]
            }
            for (const { criterion } of exclusion.criteria) {
                if (!criterion.isDisabled) {
                    const exclusionCriteria = {
                        type: criterion.type,
                        id: criterion.ids,
                        recency: criterion.recency
                    }
                    //criteria_statistics does not accept range & level for non third party data
                    if (includes(WAM_THIRD_PARTY_TYPES, criterion.type)) {
                        genericExclusion.children[0].children.push({
                            ...exclusionCriteria,
                            range: [parseInt(criterion.range), 20],
                            level: [parseInt(criterion.level), 20]
                        })
                    } else if (criterion.type === 'SmartSegment') {
                        genericExclusion.children[0].children.push({
                            ...exclusionCriteria,
                            range: criterion.range
                        })
                    } else {
                        genericExclusion.children[0].children.push(exclusionCriteria)
                    }
                }
            }
            if (exclusion.criteria.length > 0) v2Formula.children.push(genericExclusion)
        }
    }

    if (v2Formula.children.length > 0) {
        return v2Formula
    } else {
        return {}
    }
}

const helpers = {
    processStatisticsErrorHandler: (error, type, commit) => {
        commit('setIsLoading', { item: type, isLoading: false })
        if (type === 'breakdown') {
            commit('setBreakdown', { breakdown: {} })
        }
        errorHandler(error, commit)
    },

    category: params => {
        const mediaTypeList = concat(['Campaign', 'Conversion'], MEDIA_CONVERSION_TYPE, MEDIA_CAMPAIGN_TYPE)

        if (params.type === 'Audience') {
            return 'audience'
        }
        if (params.type === 'Cluster' || params.type === 'Segment') {
            return 'behavioral'
        }
        if (params.type === 'Sociodemo') {
            return 'demographic'
        }
        if (params.type === 'CustomSegment' || params.type === 'SmartSegment' || params.type === 'Techno') {
            return params.subType
        }
        if (includes(params.type, 'Division')) {
            return 'geography'
        }
        if (includes(mediaTypeList, params.type)) {
            return 'media'
        }
        if (params.type === 'GeoradarID') {
            return 'GeoradarID'
        }
    },

    v1Formula: params => {
        return formulaToV1Formula(params)
    },

    v2Formula: params => {
        return formulaToV2Formula(params)
    },

    setRecency: (criterion, hasFirstParty, formulaRecency) => {
        let recency
        //Rules: CRM segments'recency always set to 395
        if (criterion.jsonKey.subType === 'crm' || toNumber(criterion.customTaxoRecency) === 395) recency = 395
        //Rules: data & audience segments'recency always set to global recency
        else if (
            ['direct', 'data', 'custom', 'conversion'].includes(criterion.jsonKey.subType) ||
            ['WcmClick', 'WcmImpression', 'Audience'].includes(criterion.jsonKey.type)
        )
            recency = parseInt(formulaRecency)
        //Rules: for other segments: if 1st and 3rd party in formula, recency set to 30, otherwise to global recency
        else {
            recency = hasFirstParty ? 30 : parseInt(formulaRecency)
        }
        return recency
    },

    /**
     *
     * @param {Object} params
     * @param {Number|String} params.id
     * @param {String} params.label
     * @param {Object} params.jsonKey
     * @param {Number} params.display_order
     * @param {Number|String[]} params.smartSegmentIds
     * @param {Object} params.formulaSettings
     * @param {Number} params.recency
     * @returns {Object} Return a criterion with all informations for build a formula
     */
    buildCriterionForFormula: ({ id, label, jsonKey, display_order, smartSegmentIds, formulaSettings, recency, criterionSubType, range, level }) => {
        return {
            volume: null,
            criterion: {
                active: true,
                criterionSubType,
                display_order,
                hasSmartSegments: !isEmpty(smartSegmentIds),
                ids: jsonKey.ids,
                id,
                isDisabled: false,
                label,
                level: level ?? formulaSettings.settings.level ?? formulaSettings.defaultSettings.level,
                name: jsonKey.name,
                range: range ?? formulaSettings.settings.range ?? formulaSettings.defaultSettings.range,
                recency,
                selected: false,
                subType: jsonKey.subType,
                type: jsonKey.type
            }
        }
    },

    /**
     *
     * @param {Object} formula
     * @returns {Number} Count of all criteria in a formula
     */
    getFormulaSize(formula) {
        return Object.values(formula).reduce((count, boxes) => count + (boxes || []).reduce((boxCount, box) => boxCount + box.criteria.length, 0), 0)
    }
}

export default { ...helpers }
